import { inject, Injectable } from '@angular/core';
import { IProduktAuswahlDetails, PreisIndikation, PriceSheet } from '@mwe/models';
import { TariffSelectionStateService } from './tariff-selection-state.service';
import { isArrayWithMinOneItem, isPostcodeInMundlCity, isProductCategoryInternet } from '@mwe/utils';
import { ProductPriceIndicationCategory } from '@mwe/constants';
import { OrderService } from '../../order/order.service';
import { LoggingService } from '../../logging/logging.service';
import { PriceSheetService } from '../../price-sheet/price-sheet.service';
import { catchError, firstValueFrom, forkJoin, map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TariffSelectionLogic {
  private orderService = inject(OrderService);
  private priceSheetService = inject(PriceSheetService);
  private tariffSelectionStateService = inject(TariffSelectionStateService);
  private loggingService = inject(LoggingService);

  mockCategories = ['Internet', 'Iptv', 'Voip'];

  async getPriceIndications(postCode: string, categories: string[]): Promise<PreisIndikation[]> {
    let missingCategories = categories.filter(c => {
      return this.tariffSelectionStateService.indications.every(i => i.sparte.toLowerCase() !== c.toLowerCase());
    });
    const internetCategories = missingCategories.find(mc => isProductCategoryInternet(mc));

    if (internetCategories?.length > 0) {
      missingCategories = missingCategories.filter(mc => !isProductCategoryInternet(mc));
      missingCategories.push(ProductPriceIndicationCategory.SUPERSCHNELL);
    }

    if (isArrayWithMinOneItem(missingCategories)) {
      const indicationRequests = missingCategories.map(category => this.orderService.loadPriceIndications(postCode, category));
      const newIndications = (await Promise.all(indicationRequests)).flat();
      this.tariffSelectionStateService.indications = [...this.tariffSelectionStateService.indications, ...newIndications];
    }

    return this.tariffSelectionStateService.indications;
  }

  async setPriceSheetSearchesFromProducts(
    postCode: string,
    produkte: IProduktAuswahlDetails[],
    voucherName?: string,
    validTariffKeys?: string[],
  ): Promise<void> {
    const priceSheetRequests = this.getPriceSheetRequests(produkte, postCode, voucherName, validTariffKeys);

    if (priceSheetRequests?.length) {
      this.mergePriceSheets(await firstValueFrom(forkJoin(priceSheetRequests)));
    }
  }

  private mergePriceSheets(priceSheetResponses: PriceSheet[]): void {
    const existingPriceSheets = new Map(
      this.tariffSelectionStateService.priceSheets.filter(item => !!item?.isuTariffKey).map(item => [item.isuTariffKey, item]),
    );

    priceSheetResponses.forEach(priceSheet => {
      if (!priceSheet.dokumentId) {
        //failed calls only return an isuTariffKey and no dokumentId so they can be removed here
        existingPriceSheets.delete(priceSheet.isuTariffKey);
      } else {
        existingPriceSheets.set(priceSheet.isuTariffKey, priceSheet);
      }
    });

    this.tariffSelectionStateService.priceSheets = Array.from(existingPriceSheets.values());
  }

  private getPriceSheetRequests(
    produkte: IProduktAuswahlDetails[],
    postCode: string,
    voucherName?: string,
    validTariffKeys?: string[],
  ): Observable<PriceSheet>[] {
    return produkte?.map(produkt => {
      const isuTariffKey = produkt?.tarif?.ISUTarifKey;

      return this.priceSheetService
        .searchPriceSheet({
          isuTariffKey: isuTariffKey,
          deadlineDate: produkt?.bedingungen?.angebotsdatum,
          isViennaAddress: isPostcodeInMundlCity(postCode),
          voucherName: voucherName && validTariffKeys?.some(tariffKey => tariffKey === isuTariffKey) ? voucherName : undefined,
        })
        .pipe(
          map(
            result =>
              ({
                ...result,
                isuTariffKey: isuTariffKey,
                category: produkt.sparte.toLowerCase(),
              }) as PriceSheet,
          ),
          catchError(err => {
            this.loggingService.logError(err, 'PriceSheet request failed');
            return of({ isuTariffKey: isuTariffKey } as PriceSheet);
          }),
        );
    });
  }

  async getUpdatedPriceIndications(
    plz: string,
    category: string,
    tariffKey?: string,
    options?: string,
    voucherCode?: string,
  ): Promise<{
    indication: PreisIndikation;
    allIndications: PreisIndikation[];
  }> {
    const response = await this.orderService.loadPriceIndications(plz, category, tariffKey, options, voucherCode);
    return {
      indication: response?.[0],
      allIndications: response,
    };
  }

  // after tariff-options-change we need new priceIndications for this category to show updated prices
  async updatePriceIndications(plz: string, category: string, tariffKey?: string, options?: string): Promise<void> {
    if (this.mockCategories.includes(category)) {
      return Promise.resolve();
    }

    const updatedTariffIndications = await this.orderService.loadPriceIndications(plz, category, tariffKey, options);
    // remove old indications
    const indications = this.tariffSelectionStateService.indications.filter(i => {
      return updatedTariffIndications.every(ui => ui.tarif.ISUTarifKey !== i.tarif.ISUTarifKey);
    });
    // add new indications
    this.tariffSelectionStateService.indications = indications.concat(updatedTariffIndications);
  }

  wasTariffOptionsFormPopupOpenedFor(category: string): boolean {
    return this.tariffSelectionStateService.tariffOptionsFormPopupAlreadyOpenedFor.some(c => c === category);
  }

  markCategoryAsOpened(category: string): void {
    if (this.wasTariffOptionsFormPopupOpenedFor(category)) {
      return;
    }

    const categories = this.tariffSelectionStateService.tariffOptionsFormPopupAlreadyOpenedFor.concat(category);
    this.tariffSelectionStateService.tariffOptionsFormPopupAlreadyOpenedFor = categories;
  }

  hasMatchingPriceSheet(isuTariffKey: string): boolean {
    return this.priceSheetService.hasMatchingPriceSheet(isuTariffKey);
  }
}
